import { FC, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { format } from 'date-fns';
import { getPartners } from 'entities/Partners/api';
import Collapse from 'components/collapse';
import { FileType } from 'entities/Files/types';
import { retryImportFile } from 'entities/ImportSessions/api';
import { AsModalList } from 'widgets/Extensions/Details/ui/AssertionSets/ui/AsModalList';
import { useAssertionSetsStore } from 'widgets/Extensions/Details/ui/AssertionSets/model/state';
import { useCustomModelStore } from 'widgets/customModel/state';
import { AxiosError } from 'axios';
import { useFileEditorStore } from 'store/file-editor';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { useExtension } from 'shared/hooks/useExtension';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Icon } from 'shared/ui/Icon';
import { NativeSelect } from 'shared/ui/NativeSelect';
import { ExtensionsEnum } from 'entities/Extensions/enums';
import { PartnersEnum } from 'entities/Partners/enums';
import PartnersModal from '../partners-modal';

interface Props {
	file: FileType;
}

const FileInfo: FC<Props> = ({ file }) => {
	const { t } = useTranslation();
	const { fileId } = useParams();
	const { getExtension } = useExtension();
	const { assertionsSets } = useAssertionSetsStore();
	const { setFileDetailsV2 } = useFileEditorStore();
	const { setCustomFileDetails } = useCustomModelStore();
	const [isAssertionsOpen, setIsAssertionsOpen] = useState(false);
	const [isPartnersModalOpen, setIsPartnersModalOpen] = useState(false);

	const assertionSet = useMemo(
		() => assertionsSets?.find((item) => item._id === file?.assertionSetsId),
		[assertionsSets, file],
	);

	const { mutate, isLoading } = useMutation({
		mutationKey: ['patch/files/recognition-result'],
		mutationFn: retryImportFile,
	});

	const { data: partnersResponseData } = useQuery({
		queryKey: ['get/partners', file.partnerId],
		queryFn: () =>
			getPartners({
				type: null,
				partnerIds: file.partnerId ? [file.partnerId] : [],
			}),
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const updateFileAssertionsHandler = (asId?: string) => {
		const newRecognized =
			file.recognizedLabels?.map((item) => {
				if (item.type === 'table') {
					const newCells = item.cells.map((cellItem) => ({
						...cellItem,
						assertionSetsId: undefined,
						assertionsData: undefined,
					}));
					return { ...item, cells: newCells };
				}
				return { ...item, assertionSetsId: undefined, assertionsData: undefined };
			}) || [];

		mutate(
			{
				id: file?._id,
				data: {
					recognizedLabels:
						file?.assertionSetsId !== asId ? newRecognized : file?.recognizedLabels || [],
					assertionSetsId: !asId ? null : asId,
					partnerId: file?.partnerId || null,
					isPreview: true,
				},
			},
			{
				onSuccess: (response) => {
					setIsAssertionsOpen(false);
					if (asId) {
						enqueueSnackbar({
							message: t('assertions.modal.success_adding'),
							variant: 'success',
						});
					} else {
						enqueueSnackbar({
							message: t('assertions.modal.success_removing'),
							variant: 'success',
						});
					}
					if (fileId) {
						setFileDetailsV2(response.data);
					} else {
						setCustomFileDetails(response.data);
					}
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const changePartnerObtaining = (autocomplete: boolean) => {
		mutate(
			{
				id: file?._id,
				data: {
					recognizedLabels: file?.recognizedLabels || [],
					assertionSetsId: file?.assertionSetsId || null,
					partnerId: file?.partnerId || null,
					isPreview: true,
					autocomplete,
				},
			},
			{
				onSuccess: (response) => {
					enqueueSnackbar({
						message: t('partners.snackbar.success_settings_applying'),
						variant: 'success',
					});

					if (fileId) {
						setFileDetailsV2(response.data);
					} else {
						setCustomFileDetails(response.data);
					}
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const fileDetailsContent = useMemo(
		() => [
			{
				label: t('shared_texts.table_columns.imported_at'),
				content: <>{file?.createdAt ? format(new Date(file?.createdAt), 'HH:mm dd/MM/yyyy') : '-'}</>,
				visible: true,
			},
			{ label: t('shared_texts.table_columns.source'), content: file?.uploadSource, visible: true },
			{
				label: t('shared_texts.table_columns.category'),
				content: t(`sidebar.menu./files/${file?.category}`),
				visible: !!file?.category,
			},
			{
				label: t('files.details.company_info'),
				content: (
					<NativeSelect
						value={file?.autocomplete ? PartnersEnum.client : PartnersEnum.default}
						placeholder=""
						options={companyInfoOptions}
						onSelect={(e) => changePartnerObtaining(Boolean(e))}
						sx={{ width: '100%' }}
					/>
				),
				visible: true,
			},
			{
				label: t('partners.title'),
				content: (
					<>
						{file?.partnerId ? (
							partnersResponseData?.data?.results?.find((item) => item._id === file?.partnerId)
								?.name
						) : (
							<span style={{ color: ColorPalette.violetRed }}>
								{t('shared_texts.not_found')}
							</span>
						)}
						<Box onClick={() => setIsPartnersModalOpen(true)} sx={{ marginLeft: '40px' }}>
							<Icon id={'choose_subcategory'} style={{ cursor: 'pointer' }} />
						</Box>
					</>
				),
				visible: true,
			},
			{
				label: t('extensions.ex_list.assertions_sets.title'),
				content: (
					<>
						{assertionSet?.name || t('shared_texts.not_found')}
						<Box onClick={() => setIsAssertionsOpen(true)} sx={{ marginLeft: '40px' }}>
							<Icon id={'choose_subcategory'} style={{ cursor: 'pointer' }} />
						</Box>
					</>
				),
				visible: fileId && getExtension(ExtensionsEnum.assertions_sets)?.isEnabled,
			},
		],
		[file, fileId, partnersResponseData],
	);

	return (
		<>
			<AsModalList
				open={isAssertionsOpen}
				handleApply={updateFileAssertionsHandler}
				handleRemoveAssertions={updateFileAssertionsHandler}
				handleClose={() => setIsAssertionsOpen(false)}
				loading={isLoading}
				defaultAssertionId={file?.assertionSetsId}
			/>
			<PartnersModal
				open={isPartnersModalOpen}
				handleClose={() => setIsPartnersModalOpen(false)}
				file={file}
			/>

			<Collapse label={t('files.details.files')}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
					{fileDetailsContent.map((item, index) =>
						item.visible ? (
							<Box
								key={`details-${index}`}
								sx={{
									display: 'flex',
									alignItems: 'center',
								}}
							>
								<Typography
									variant={'body2'}
									color={ColorPalette.baliHai}
									sx={{ display: 'flex', alignItems: 'center', flex: '1 1' }}
								>
									{item.label}
								</Typography>
								<Typography
									variant={'body2'}
									color={ColorPalette.astronaut}
									sx={{ display: 'flex', alignItems: 'center', flex: '1 1' }}
								>
									{item.content}
								</Typography>
							</Box>
						) : null,
					)}
				</Box>
			</Collapse>
		</>
	);
};

export default FileInfo;

const companyInfoOptions = [
	{
		value: PartnersEnum.default,
		label: 'files.select_options.obtained_from_doc',
	},
	{
		value: PartnersEnum.client,
		label: 'files.select_options.obtained_from_partner',
	},
];
